// Crisp anime lightning (owner 2026-11-02: 'put electricity around the [Pulse Nova] energy field'). The shared Arcs renderer
// draws soft additive glow boxes that bloom into smoke at the gameplay camera; this draws each bolt as a flat camera-facing
// ribbon with a hard white core and a coloured edge (cel look), jagged per segment, flickering and fading fast.
// One pooled dynamic mesh; no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, attribute, color, float, mix, smoothstep, uv, vec3 } from 'three/tsl';
import { uploadPrefix } from './uploadTrim';
import { FLASH } from './a11y';

const MAX_SEG = 900;
interface Seg { ax: number; ay: number; az: number; bx: number; by: number; bz: number; t: number; life: number; w: number; r: number; g: number; b: number }

export class Lightning {
  readonly mesh: THREE.Mesh;
  private segs: Seg[] = [];
  /** recycled segment records (pooled: no allocation once warm) */
  private free: Seg[] = [];
  private pos: Float32Array;
  private col: Float32Array;
  private cap: number;
  private geo = new THREE.BufferGeometry();
  private c = new THREE.Color();

  /** overlay = draw over everything (no depth test): for effects that must read inside an opaque fireball */
  /** soft: an additive glow ribbon (no white core, brightness falling off from the centre line) for halos under a bolt */
  constructor(scene: THREE.Scene, overlay = false, coreW = 0.42, gain = 1.25, cap = MAX_SEG, soft = false) { // coreW: half-width of the white-hot core (0..1 of the ribbon); gain: colour multiplier (>1 blooms); cap: pooled segments
    this.cap = cap; this.pos = new Float32Array(cap * 4 * 3); this.col = new Float32Array(cap * 4 * 4);
    const uvs = new Float32Array(cap * 4 * 2), idx = new Uint32Array(cap * 6);
    for (let i = 0; i < cap; i++) {
      uvs.set([0, 0, 1, 0, 0, 1, 1, 1], i * 8);
      idx.set([i * 4, i * 4 + 1, i * 4 + 2, i * 4 + 2, i * 4 + 1, i * 4 + 3], i * 6);
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('bolt', new THREE.BufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.setDrawRange(0, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const bolt = attribute('bolt', 'vec4');          // rgb = edge colour, a = fade
    const across = abs(uv().y.sub(0.5)).mul(2);      // 0 at the centre line, 1 at the ribbon edge
    const core = smoothstep(coreW, coreW * 0.7, across); // hard white core
    if (soft) {
      const fall = float(1).sub(across), glow = fall.mul(fall).mul(bolt.w);
      m.colorNode = vec3(bolt.x, bolt.y, bolt.z).mul(glow).mul(gain); m.opacityNode = float(1);
      m.blending = THREE.AdditiveBlending;
    } else {
      m.colorNode = mix(vec3(bolt.x, bolt.y, bolt.z), color(0xffffff), core).mul(gain);
      m.opacityNode = smoothstep(1.0, 0.82, across).mul(bolt.w).mul(float(1));
    }
    if (overlay) m.depthTest = false;
    this.mesh = new THREE.Mesh(this.geo, m); this.mesh.frustumCulled = false; this.mesh.renderOrder = overlay ? 7 : 5;
    scene.add(this.mesh);
  }

  /** a jagged bolt through the given points (x,y,z triples); jag = max sideways kink per subdivision */
  add(pts: number[], hex: number, width = 0.16, life = 0.12, jag = 0.45): void {
    this.c.setHex(hex);
    for (let i = 0; i + 5 < pts.length; i += 3) {
      let px = pts[i], py = pts[i + 1], pz = pts[i + 2];
      const ex = pts[i + 3], ey = pts[i + 4], ez = pts[i + 5];
      for (let s = 1; s <= 3; s++) {
        const k = s / 3;
        let nx = px + (ex - px) * (1 / (4 - s)), ny = py + (ey - py) * (1 / (4 - s)), nz = pz + (ez - pz) * (1 / (4 - s));
        if (s < 3) { nx += (Math.random() - 0.5) * jag; ny += (Math.random() - 0.5) * jag; nz += (Math.random() - 0.5) * jag; } else { nx = ex; ny = ey; nz = ez; }
        void k;
        if (this.segs.length < this.cap) this.put(px, py, pz, nx, ny, nz, life, width);
        px = nx; py = ny; pz = nz;
      }
    }
  }

  /** a bolt through exactly these points (already jagged by the caller: no extra subdivision) */
  addRaw(pts: number[], hex: number, width: number, life: number): void {
    this.c.setHex(hex);
    for (let i = 0; i + 5 < pts.length && this.segs.length < this.cap; i += 3)
      this.put(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], pts[i + 4], pts[i + 5], life, width);
  }
  private put(ax: number, ay: number, az: number, bx: number, by: number, bz: number, life: number, w: number): void {
    const s = this.free.pop() ?? { ax: 0, ay: 0, az: 0, bx: 0, by: 0, bz: 0, t: 0, life: 0, w: 0, r: 0, g: 0, b: 0 };
    s.ax = ax; s.ay = ay; s.az = az; s.bx = bx; s.by = by; s.bz = bz; s.t = 0; s.life = life; s.w = w; s.r = this.c.r; s.g = this.c.g; s.b = this.c.b;
    this.segs.push(s);
  }
  get live(): number { return this.segs.length; }
  /** pre-allocate n segment records so the first big strike allocates nothing */
  prewarm(n: number): void { for (let i = this.free.length; i < n; i++) this.free.push({ ax: 0, ay: 0, az: 0, bx: 0, by: 0, bz: 0, t: 0, life: 0, w: 0, r: 0, g: 0, b: 0 }); }

  update(dt: number, cam: THREE.Vector3): void {
    let n = 0;
    for (let k = 0; k < this.segs.length; k++) {
      const s = this.segs[k]; s.t += dt;
      if (s.t > s.life) { this.free.push(s); this.segs[k] = this.segs[this.segs.length - 1]; this.segs.pop(); k--; continue; }
      // ribbon side = normalize(cross(segment dir, view dir)) * width/2
      const dx = s.bx - s.ax, dy = s.by - s.ay, dz = s.bz - s.az;
      const vx = cam.x - s.ax, vy = cam.y - s.ay, vz = cam.z - s.az;
      let sx = dy * vz - dz * vy, sy = dz * vx - dx * vz, sz = dx * vy - dy * vx;
      const sl = Math.hypot(sx, sy, sz) || 1, f = 1 - s.t / s.life, hw = s.w * 0.5 * (0.6 + 0.4 * f);
      sx = sx / sl * hw; sy = sy / sl * hw; sz = sz / sl * hw;
      const p = this.pos, o = n * 12;
      p[o] = s.ax - sx; p[o + 1] = s.ay - sy; p[o + 2] = s.az - sz;
      p[o + 3] = s.bx - sx; p[o + 4] = s.by - sy; p[o + 5] = s.bz - sz;
      p[o + 6] = s.ax + sx; p[o + 7] = s.ay + sy; p[o + 8] = s.az + sz;
      p[o + 9] = s.bx + sx; p[o + 10] = s.by + sy; p[o + 11] = s.bz + sz;
      // a11y: Reduced / Minimal flashes drop the per-frame random flicker (a strobe) and dim the bolt
      const flick = FLASH.mode === 'off' ? f * (0.75 + Math.random() * 0.25) : f * 0.9 * FLASH.L.bolt;
      for (let v = 0; v < 4; v++) { const c = (n * 4 + v) * 4; this.col[c] = s.r; this.col[c + 1] = s.g; this.col[c + 2] = s.b; this.col[c + 3] = flick; }
      n++;
    }
    this.geo.setDrawRange(0, n * 6);
    uploadPrefix(n * 4, this.geo.attributes.position as THREE.BufferAttribute, this.geo.attributes.bolt as THREE.BufferAttribute); // PERF-STRESS: live part only
  }
  clear(): void { for (const s of this.segs) this.free.push(s); this.segs.length = 0; this.geo.setDrawRange(0, 0); }
}
